import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosRequest from '../utils/axios';
import qs from 'qs';

export type WinnersQueryOptions = any;

export interface Prize {
    id: number;
    code: string;
    name: string;
    type: string;
    label: string;
    quantity: number;
    codeExpirationDate: string;
}

export interface Consumer {
    firstName: string;
    lastName: string;
    segment: string;
    segmentType: string;
}

export interface Prizes {
    id: number | null;
    prize: string | null;
    prize_details?: {
        code?: string;
        codeExpirationDate?: string;
        prize: Prize;
        consumer: Consumer;
    };
}

export interface WinnersState {
    listWinners: {
        data: Array<{
            first_name: string | null;
            last_name: string | null;
            address: string | null;
            city: null | string;
            county: null | string;
            prize: null | string;
            id: null | number;
        }>;
        current_page: number;
        per_page: number;
        total: number;
        pagination: {
            currentPage: number;
            lastPageNb: number;
            nbOnPage: number;
        };
    };
    listInstantWinners: {
        data: Array<{
            first_name: string | null;
            last_name: string | null;
            address: string | null;
            city: null | string;
            county: null | string;
            prize: null | string;
            id: null | number;
        }>;
        current_page: number;
        per_page: number;
        total: number;
        pagination: {
            currentPage: number;
            lastPageNb: number;
            nbOnPage: number;
        };
    };
    filters: {
        prizes: [{ prize: string | null }];
        cities: [{ city: string | null }];
    };
    winnerData: {
        id: number | null;
        token: string | null;
        first_name: string | null;
        last_name: string | null;
        phone_number: string | null;
        email: string | null;
        prize: string | null;
        city: string | null;
        county: string | null;
        ops_gift_id: string | null;
        invitation_code: string | null;
        is_redeemed: boolean | null;
        image: string | null;
        created_at: string | null;
        updated_at: string | null;
        street: string | null;
        street_number: string | null;
        block: string | null;
        floor: string | null;
        apartment: string | null;
        additionalInfo: string | null;
        marketingAgreement: boolean;
    };
    getInstantWinnersLoading: boolean;
    getInstantWinnersSuccess: boolean;
    getInstantWinnersError: string | null;
    getWinnersLoading: boolean;
    getWinnersSuccess: boolean;
    getWinnersError: string | null;
    getWinnersFiltersLoading: boolean;
    getWinnersFiltersSuccess: boolean;
    getWinnersFiltersError: string | null;

    getPrizesLoading: boolean;
    getPrizesSuccess: boolean;
    getPrizesError: string | null;
    prizes: [
        {
            expired_at: any;
            id: number | null;
            token: string | null;
            first_name: string | null;
            last_name: string | null;
            phone_number: string | null;
            prize: string | null;
            city: string | null;
            telcor_id: string | null;
            county: string | null;
            ops_gift_id: string | null;
            invitation_code: string | null;
            is_redeemed: boolean | null;
            image: string | null;
            created_at: string | null;
            updated_at: string | null;
            status: any;
            prize_details?: {
                code?: string;
                codeExpirationDate?: string;
                prize: Prize;
                consumer: Consumer;
            };
        }
    ];
}

interface AxiosError {
    response?: {
        statusMessage?: string;
        data?: {
            message?: string;
            invalidField?: {
                fieldErrorDescription?: string;
            };
        };
    };
    message: string;
}

const initialState: WinnersState = {
    listWinners: {
        data: [],
        current_page: 1,
        per_page: 5,
        total: 1,
        pagination: {
            currentPage: 1,
            lastPageNb: 0,
            nbOnPage: 10
        }
    },
    listInstantWinners: {
        data: [],
        current_page: 1,
        per_page: 5,
        total: 1,
        pagination: {
            currentPage: 1,
            lastPageNb: 0,
            nbOnPage: 10
        }
    },
    filters: {
        prizes: [{ prize: null }],
        cities: [{ city: null }]
    },
    winnerData: {
        id: null,
        token: null,
        first_name: null,
        last_name: null,
        phone_number: null,
        email: null,
        prize: null,
        city: null,
        county: null,
        ops_gift_id: null,
        invitation_code: null,
        is_redeemed: null,
        image: null,
        created_at: null,
        updated_at: null,
        street: null,
        street_number: null,
        block: null,
        floor: null,
        apartment: null,
        additionalInfo: null,
        marketingAgreement: false
    },
    getInstantWinnersLoading: false,
    getInstantWinnersSuccess: false,
    getInstantWinnersError: null,
    getWinnersLoading: false,
    getWinnersSuccess: false,
    getWinnersError: null,
    getWinnersFiltersLoading: false,
    getWinnersFiltersSuccess: false,
    getWinnersFiltersError: null,

    //getPrizes
    getPrizesLoading: false,
    getPrizesSuccess: false,
    getPrizesError: null,
    prizes: [
        {
            expired_at: null,
            id: null,
            token: null,
            first_name: null,
            last_name: null,
            phone_number: null,
            prize: null,
            city: null,
            telcor_id: null,
            county: null,
            ops_gift_id: null,
            invitation_code: null,
            is_redeemed: null,
            image: null,
            status: null,
            created_at: null,
            updated_at: null
        }
    ]
};

export const getInstantWinners = createAsyncThunk(
    'instant-winners',
    async (values: WinnersQueryOptions, { rejectWithValue }) => {
        const options = qs.stringify(values, { skipNulls: true });

        try {
            const response = await axiosRequest.get(`/reward-winners?${decodeURI(options)}`);
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const getWeeklyWinners = createAsyncThunk(
    'winners',
    async (values: WinnersQueryOptions, { rejectWithValue }) => {
        const options = qs.stringify(values, { skipNulls: true });

        try {
            const response = await axiosRequest.get(`winners?${decodeURI(options)}`);
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const getWinnersFilters = createAsyncThunk(
    'winners/filters',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosRequest.get('winners/filters');
            return response.data;
        } catch (e) {
            const error = e as AxiosError;
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const getPrizes = createAsyncThunk('prizes', async (params: any, { rejectWithValue }) => {
    try {
        const response = await axiosRequest.get(`prizes`, { params });
        return response.data.prizes;
    } catch (e) {
        const error = e as AxiosError;
        return rejectWithValue(error.response?.data?.message || error.message);
    }
});

export const winnersSlice = createSlice({
    name: 'listWinners',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getInstantWinners.pending, (state) => {
                state.getInstantWinnersLoading = true;
                state.getInstantWinnersSuccess = false;
                state.getInstantWinnersError = null;
            })
            .addCase(getInstantWinners.fulfilled, (state, action) => {
                state.listInstantWinners = action.payload;
                state.getInstantWinnersLoading = false;
                state.getInstantWinnersSuccess = true;
                state.getInstantWinnersError = null;
            })
            .addCase(getInstantWinners.rejected, (state, action) => {
                state.getInstantWinnersLoading = false;
                state.getInstantWinnersSuccess = false;
                state.getInstantWinnersError = action.payload as string;
            })
            .addCase(getWeeklyWinners.pending, (state) => {
                state.getWinnersLoading = true;
                state.getWinnersSuccess = false;
                state.getWinnersError = null;
            })
            .addCase(getWeeklyWinners.fulfilled, (state, action) => {
                state.listWinners = action.payload;
                state.getWinnersLoading = false;
                state.getWinnersSuccess = true;
                state.getWinnersError = null;
            })
            .addCase(getWeeklyWinners.rejected, (state, action) => {
                state.getWinnersLoading = false;
                state.getWinnersSuccess = false;
                state.getWinnersError = action.payload as string;
            })

            .addCase(getWinnersFilters.pending, (state) => {
                state.getWinnersFiltersLoading = true;
                state.getWinnersFiltersSuccess = false;
                state.getWinnersFiltersError = null;
            })
            .addCase(getWinnersFilters.fulfilled, (state, action) => {
                state.filters = action.payload;
                state.getWinnersFiltersLoading = false;
                state.getWinnersFiltersSuccess = true;
                state.getWinnersFiltersError = null;
            })
            .addCase(getWinnersFilters.rejected, (state, action) => {
                state.getWinnersFiltersLoading = false;
                state.getWinnersFiltersSuccess = false;
                state.getWinnersFiltersError = action.payload as string;
            })

            .addCase(getPrizes.pending, (state) => {
                state.getPrizesLoading = true;
                state.getPrizesSuccess = false;
                state.getPrizesError = null;
            })
            .addCase(getPrizes.fulfilled, (state, action) => {
                state.prizes = action.payload;
                state.getPrizesLoading = false;
                state.getPrizesSuccess = true;
                state.getPrizesError = null;
            })
            .addCase(getPrizes.rejected, (state, action) => {
                state.getPrizesLoading = false;
                state.getPrizesSuccess = false;
                state.getPrizesError = action.payload as string;
            });
    }
});

export default winnersSlice.reducer;
